import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box, Container, FormControl, Grid, Typography, TextField, IconButton,
  Button, Link, CircularProgress,
  Alert
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import currency from "currency.js";
import moment from "moment";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBack from '@mui/icons-material/ArrowBack';
import DeleteIcon from "@mui/icons-material/Delete";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";

import { getContract, updateContract, updateContractResult, HandlePdfChargeAccount } from "../../services/backend/ContractService";
import usePaymentCutoff from "./usePaymentCutoff";
import PaymentCutoffNotice from "./PaymentCutoffNotice";
import { DeleteFile, UploadFile } from "../../services/backend/UploadFileService";

import MoneyInput from '../generic/MoneyInput';
import ChargeAccountModal from "./ChargeAccountModal";
import SocialSecurityModal from "./SocialSecurityModal";
import ChargeAccountTemplate from "./ChargeAccountTemplate";
import { ARL, CONTRACT_STATUS, PERMISSIONS, REVIEW_STATUS } from "../../constants";
import DialogConfirmation from "../generic/DialogConfirmation";
import DialogSignConfirmation from "../generic/DialogSignConfirmation";
import MyBackdrop from "../generic/MyBackdrop";
import MySnackbar from "../generic/MySnackbar";
import { assertPdfNotEncrypted } from "../../services/pdf/validatePdf";
import {
  getContractField, buildChargeAccountDataSource, getActivityStatus,
  getPeriodDateInfo, collectPeriodNews, getLastApprovedPaymentIdx,
  buildRevertedApprovalPayment, areAffiliationCertsLocked
} from "../../functions";
import ContractViewPaymentPlan from "./ContractViewPaymentPlan";

const ContractView = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const users = useSelector((state) => state.user.users);
  const { contractId } = useParams();
  const [contract, setContract] = useState();
  const [certificates, setCertificates] = useState({ eps: null, afp: null, arl: null });
  const [showChargeAccountModal, setShowChargeAccountModal] = useState(false);
  const [showSocialSecurity, setShowSocialSecurity] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmInfo, setConfirmInfo] = useState({ open: false });
  const [approvedInfo, setApprovedInfo] = useState({ open: false });
  const [revertInfo, setRevertInfo] = useState({ open: false });
  const [revertNote, setRevertNote] = useState('');
  const [openBackdrop, setOpenBackdrop] = useState();
  const [infoSnack, setInfoSnack] = useState({ open: false });

  const isContractor = useMemo(() => contract?.contractor?.userId === user.userId, [contract, user]);
  // Fecha de corte (payment-monthly-cutoff): deshabilita la aprobación en el
  // plan de pagos y alimenta el aviso. ADMIN exento; el backend revalida.
  const paymentCutoff = usePaymentCutoff();
  const cutoffReason = paymentCutoff.blockReason(contract?.nature);

  const getContractFieldWithNews = useCallback((key, limitDate) => {
    return getContractField(contract?.news, contract, limitDate)(key);
  }, [contract]);

  const { status, supervisors } = useMemo(() => ({
    status: getContractFieldWithNews('status'),
    supervisors: getContractFieldWithNews('supervisors')
  }), [getContractFieldWithNews]);

  // Certificados de afiliación bloqueados con la cuenta del primer periodo
  // aprobada: entran en su consolidado. Se desbloquean devolviendo esa cuenta.
  const affiliationLocked = useMemo(() => areAffiliationCertsLocked(
    getContractFieldWithNews('paymentPlan'), contract?.payments
  ), [getContractFieldWithNews, contract?.payments]);

  const balance = useMemo(() => {
    if (!contract?.contractBudget) return 0;
    const contractBudget = getContractFieldWithNews('contractBudget');
    const paymentPlan = getContractFieldWithNews('paymentPlan');

    return contractBudget - paymentPlan?.reduce((a, b) => a = a + b, 0);
  }, [contract?.contractBudget, getContractFieldWithNews]);

  // Fechas del periodo `idx` y, si se pasa `newsArr`, las novedades que lo
  // tocan (de ahí sale el aviso de la fila del plan de pagos). El rango sale
  // de los días EJECUTABLES del plan vigente: un mes que una suspensión
  // recortó no se rotula como mes completo. Devuelve null en los meses en
  // cero, que no se rotulan porque no son periodos de pago.
  const getPeriodInfo = (idx, newsArr) => {
    const info = getPeriodDateInfo(contract, getContractFieldWithNews('paymentPlanDays'), idx);

    if (newsArr) {
      collectPeriodNews(
        contract,
        getContractFieldWithNews('paymentPlan'),
        getContractFieldWithNews('finishedAt'),
        idx,
        newsArr
      );
    }

    return info;
  };

  const handleUploadCertificate = async (evt, key) => {
    if (evt.target.files.length === 0 || affiliationLocked) return;

    const file = evt.target.files[0];
    const encryptedError = await assertPdfNotEncrypted(file);
    if (encryptedError) {
      evt.target.value = '';
      setInfoSnack({ open: true, message: encryptedError, severity: 'error', duration: 5000 });
      return;
    }

    setLoading(true);

    const response = await UploadFile(
      user,
      file,
      process.env.REACT_APP_STORAGE_BUCKET_CONTRACTS,
      `${contractId}/certificado_${key}.pdf`
    );

    const certs = { ...certificates };
    certs[key] = response.downloadUrl;
    setCertificates(certs);
    // Sincroniza también el objeto contract: el gate de envío a revisión del
    // mes 0 (ChargeAccountModal) lee contract.certificates.
    setContract({ ...contract, certificates: certs });
    await updateContract(contractId, { certificates: certs }, user);
    setLoading(false);
  };

  const handleRemoveCertificate = async (result, data) => {
    if (!result || !data?.key || affiliationLocked) return setConfirmInfo({ open: false });
    setLoading(true);

    await DeleteFile(
      user,
      process.env.REACT_APP_STORAGE_BUCKET_CONTRACTS,
      `${contractId}/certificado_${data.key}.pdf`
    );

    const certs = { ...certificates };
    certs[data.key] = null;
    setCertificates(certs);
    // Sincroniza también el objeto contract: al borrar un certificado, el gate
    // de envío a revisión del mes 0 debe volver a bloquearse sin recargar.
    setContract({ ...contract, certificates: certs });
    await updateContract(contractId, { certificates: certs }, user);
    setLoading(false);
    setConfirmInfo({ open: false })
  };

  const handleContractApproved = async () => {
    setOpenBackdrop("Aprobando contrato");

    const email = {
      emailTo: contract.contractor.email,
      template: 'contractSupervisorApproved',
      data: {
        contractCode: contractId,
        usertitle: contract.contractor.usertitle,
        fullname: `${contract.contractor.name} ${contract.contractor.lastName}`,
        position: contract.contractor.position,
        entity: contract.contractor.entity,
        dependence: contract.contractor.dependence,
        supervisorName: `${supervisors[0].name} ${supervisors[0].lastName}`
      }
    };

    await updateContract(contractId, { status: CONTRACT_STATUS.APPROVED, email }, user);
    setContract({ ...contract, status: CONTRACT_STATUS.APPROVED });
    setOpenBackdrop(undefined);
  };

  const handleChargeAccountApproved = async (result, data) => {
    if (!result || !data?.payment) return setApprovedInfo({ open: false });
    setApprovedInfo({ open: false });
    setOpenBackdrop("Aprobando cuenta de cobro");
    
    let email;
    let payments = {...contract.payments};
    // Copia del periodo, no mutación: si el backend rechaza (fecha de corte),
    // el contrato en memoria debe quedar como estaba.
    if (payments[data.payment]) payments[data.payment] = { ...payments[data.payment] };

    // Aprobación vacía (periodo sin actividades, solo ADMIN): queda registrada
    // en el periodo con su autor y no notifica al contratista, que nunca
    // trabajó ese mes. Ver el atajo de aprobación en ContractViewPaymentPlan.
    const emptyApprovalMark = data.emptyApproval
      ? {
        approvedEmpty: true,
        approvedEmptyBy: {
          userId: user.userId,
          name: `${user.name} ${user.lastName}`,
          at: new Date().toISOString(),
        },
      }
      : {};

    if (payments[data.payment]) {
      payments[data.payment].approved = true;
      payments[data.payment].approvedAt = new Date().toISOString();
      Object.assign(payments[data.payment], emptyApprovalMark);

      // La aprobación del acta por el Supervisor aprueba las actividades no
      // devueltas. La seguridad social NO se toca aquí: su estado terminal es
      // Revisada (marca del Supervisor o del Apoyo en el modal de SS) y es
      // prerrequisito de esta aprobación — SS del mes anterior y, con pago
      // anticipado, también la del mes (gating en ContractViewPaymentPlan).
      const pay = payments[data.payment];
      if (pay.activities) {
        pay.activities = pay.activities.map((act) => (
          getActivityStatus(act) === REVIEW_STATUS.RETURNED
            ? act
            : { ...act, reviewStatus: REVIEW_STATUS.APPROVED }
        ));
      }

      const idx = parseInt(data.payment.split('_')[1], 10);

      // La aprobación vacía no notifica: el contratista no presentó nada en
      // ese periodo y el correo de cuenta aprobada solo lo confundiría.
      if (!data.emptyApproval) {
        email = {
          emailTo: contract.contractor.email,
          template: 'chargeAccountSupervisorApproved',
          data: {
            contractCode: contractId,
            usertitle: contract.contractor.usertitle,
            fullname: `${contract.contractor.name} ${contract.contractor.lastName}`,
            position: contract.contractor.position,
            entity: contract.contractor.entity,
            dependence: contract.contractor.dependence,
            supervisorName: `${supervisors[0].name} ${supervisors[0].lastName}`,
            rangeDate: getPeriodInfo(idx)?.range
          }
        };
      }
    } else {
      payments[data.payment] = {
        approved: true,
        approvedAt: new Date().toISOString(),
        ...emptyApprovalMark,
      };
    }

    const updateResult = await updateContractResult(contractId, { payments, email }, user);
    if (!updateResult.ok) {
      // El backend es la autoridad (p. ej. 409 por fecha de corte).
      setOpenBackdrop(undefined);
      setInfoSnack({
        open: true,
        severity: 'error',
        duration: 6000,
        message: updateResult.message || 'No fue posible aprobar la cuenta de cobro',
      });
      return;
    }
    const updatedContract = { ...contract, payments };
    setContract(updatedContract);

    // La aprobación vacía no produce cuenta de cobro: el periodo se cierra
    // solo para desbloquear el encadenamiento de los meses siguientes, y un PDF
    // sin actividades no es un documento que deba existir ni firmarse.
    if (data.emptyApproval) {
      setOpenBackdrop(undefined);
      return;
    }

    setOpenBackdrop("Generando cuenta de cobro en PDF");

    try {
      const { dataSource } = buildChargeAccountDataSource({
        contract: updatedContract, payment: data.payment, users, getContractFieldWithNews
      });
      const response = await HandlePdfChargeAccount(dataSource, user);
      if (response?.result && response.downloadUrl) {
        await updateContract(contractId, {
          [`payments.${data.payment}.chargeAccount`]: response.downloadUrl,
          [`payments.${data.payment}.chargeAccountHash`]: response.fileHash,
          [`payments.${data.payment}.hashFileUrl`]: response.hashDownloadUrl
        }, user);
        setContract(prev => ({
          ...prev,
          payments: {
            ...prev.payments,
            [data.payment]: { ...prev.payments[data.payment], chargeAccount: response.downloadUrl, hashFileUrl: response.hashDownloadUrl }
          }
        }));
      }
    } catch (error) {
      console.error('Error al generar PDF:', error);
    }

    setOpenBackdrop(undefined);
  };

  // Reversa de la aprobación de la ÚLTIMA cuenta de cobro aprobada (permiso
  // REVERT_APPROVAL, por defecto solo ADMIN). Deja el acta de nuevo en
  // revisión del Supervisor, que puede devolver actividades al contratista o
  // volver a aprobarla. Las anteriores se revierten en secuencia: revertida
  // esta, la previa pasa a ser la última aprobada. No notifica por correo.
  const handleChargeAccountRevert = async (result, data) => {
    if (!result || !data?.payment) {
      setRevertInfo({ open: false });
      setRevertNote('');
      return;
    }

    const reason = revertNote.trim();
    if (!reason) {
      setInfoSnack({ open: true, message: 'El motivo es requerido para devolver la cuenta de cobro', severity: 'warning' });
      return;
    }

    setRevertInfo({ open: false });
    setRevertNote('');

    // Revalida contra el estado vigente: el diálogo pudo quedar abierto
    // mientras cambiaba el plan o se aprobaba otro periodo.
    const idx = parseInt(data.payment.split('_')[1], 10);
    if (getLastApprovedPaymentIdx(getContractFieldWithNews('paymentPlan'), contract.payments) !== idx) {
      setInfoSnack({ open: true, message: 'Solo se puede devolver la última cuenta de cobro aprobada', severity: 'error' });
      return;
    }

    setOpenBackdrop("Devolviendo cuenta de cobro");

    const reverted = buildRevertedApprovalPayment(
      contract.payments[data.payment],
      { userId: user.userId, name: `${user.name} ${user.lastName}` },
      reason
    );

    // Se reemplaza solo el mapa de este periodo: `update` con la ruta
    // `payments.payment_N` sustituye el mapa completo, así que los campos
    // retirados desaparecen del documento.
    const response = await updateContract(contractId, { [`payments.${data.payment}`]: reverted }, user);
    setOpenBackdrop(undefined);

    if (!response) {
      setInfoSnack({ open: true, message: 'No se pudo devolver la cuenta de cobro', severity: 'error' });
      return;
    }

    setContract((prev) => ({ ...prev, payments: { ...prev.payments, [data.payment]: reverted } }));
    setInfoSnack({ open: true, message: 'Cuenta de cobro devuelta a revisión del Supervisor', severity: 'success' });
  };

  useEffect(() => {
    (async () => {
      if (!contract) {
        if (!user.permissions.includes(PERMISSIONS.CONTRACT.READ)) {
          return navigate("/home");
        }

        const response = await getContract(contractId, user);
        if (!response) return navigate("/home");

        let tSupervisors = [...response.supervisors];
        const now = moment();
        response.news?.forEach((n) => {
          if (
            (n.type === "CHANGE_SUPERVISOR"
              && n.startedAt && now.isSameOrAfter(n.startedAt)
            ) || (
              n.type === 'ASSIGN_SUPERVISOR'
              && n.startedAt && now.isSameOrAfter(n.startedAt)
              && n.finishedAt && now.isSameOrBefore(n.finishedAt)
            )
          ) {
            tSupervisors = n.supervisor ? [n.supervisor] : supervisors;
          }
        });
         
        if (
          (!user.roles.includes('ADMIN') && response.status !== CONTRACT_STATUS.APPROVED)
          || (
              !user.roles.includes('ADMIN')
              && !user.roles.includes('ADMIN_CONTRACT')
              && !(response.contractor?.userId === user.userId)
              && !tSupervisors?.map(s => s.userId).includes(user.userId)
              && !response.supportIDs?.includes(user.userId)
          )) {
          return navigate("/home");
        }

        setContract(response);
        if (response.certificates) setCertificates(response.certificates);
      }
    })();
  }, []); // eslint-disable-line

  return !contract ? null : (
    <Container sx={{ p: 2 }}>
      <MyBackdrop open={!!openBackdrop} title={openBackdrop} />
      <MySnackbar
        info={infoSnack}
        handleClose={() => setInfoSnack({ open: false })}
      />

      <ChargeAccountModal
        open={!!showChargeAccountModal}
        payment={showChargeAccountModal}
        onClose={() => setShowChargeAccountModal(false)}
        contract={contract}
        setContract={setContract}
      />
      <SocialSecurityModal
        open={!!showSocialSecurity}
        payment={showSocialSecurity}
        onClose={() => setShowSocialSecurity(false)}
        contract={contract}
        setContract={setContract}
        getContractFieldWithNews={getContractFieldWithNews}
      />
      <ChargeAccountTemplate
        contract={{ ...contract }}
        getContractFieldWithNews={getContractFieldWithNews}
        payment={showTemplate}
        open={!!showTemplate}
        onClose={() => setShowTemplate(false)}
      />
      <DialogConfirmation
        confirmInfo={confirmInfo}
        handleCloseDialog={handleRemoveCertificate}
      />
      <DialogSignConfirmation
        confirmInfo={approvedInfo}
        handleCloseDialog={handleChargeAccountApproved}
      />
      <DialogConfirmation
        confirmInfo={revertInfo}
        handleCloseDialog={handleChargeAccountRevert}
        content={(
          <>
            {revertInfo.warning && (
              <Alert severity="warning" sx={{ mb: 2, maxWidth: 480 }}>
                {revertInfo.warning}
              </Alert>
            )}
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Motivo (requerido)"
              placeholder="Describa el motivo por el que se devuelve la cuenta de cobro aprobada"
              value={revertNote}
              onChange={(evt) => setRevertNote(evt.target.value)}
              sx={{ mt: 1, minWidth: 360 }}
            />
          </>
        )}
      />

      {status === CONTRACT_STATUS.SUSPENDED
        && <Alert severity="info" sx={{ my: 2, margin: 'auto' }}>
          Contrato Suspendido
        </Alert>
      }
      {status === CONTRACT_STATUS.FINISHED
        && <Alert severity="warning" sx={{ my: 2, margin: 'auto' }}>
          Contrato Terminado
        </Alert>
      }
      {status === CONTRACT_STATUS.CLOSED
        && <Alert severity="warning" sx={{ my: 2, margin: 'auto' }}>
          Contrato Cerrado
        </Alert>
      }

      <Typography variant="h4">
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          style={{ margin: 16 }}
          onClick={() => navigate(-1)}
        >
          Volver
        </Button>
        Seguimiento a contrato: { contract.contractCode }
      </Typography>
      {(isContractor || user.permissions.includes(PERMISSIONS.SUPERVISOR_CONTRACT.APPROVE)) && (
        <PaymentCutoffNotice
          cutoff={paymentCutoff}
          natures={[contract.nature]}
          role={isContractor ? 'contractor' : 'supervisor'}
        />
      )}
      <Grid item xs={12} sx={{ my: 3, color: "gray" }}>
        <Typography variant="h6">{contract.contractType}</Typography>
      </Grid>
      <Box sx={{ color: "gray", my: 3 }}>
        Objeto del contrato: {contract?.contractSubject}
      </Box>
      <Grid container spacing={1}>
        <Grid item xs={3}>
          <FormControl fullWidth sx={{ marginY: 1 }}>
            <TextField
              label="Código del contrato"
              defaultValue={contract.contractCode}
              InputProps={{ readOnly: true }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth sx={{ marginY: 1 }}>
            <TextField
              label="Nombre Contratista"
              defaultValue={contract.contractor ? `${contract.contractor.name} ${contract.contractor.lastName}` : ''}
              InputProps={{ readOnly: true }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth sx={{ marginY: 1 }}>
            <TextField
              label="Número de identificación"
              defaultValue={`${contract.contractor?.nuip || contract.contractorNuip}`}
              InputProps={{ readOnly: true }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth sx={{ marginY: 1 }}>
            <TextField
              label="Supervisor"
              defaultValue={supervisors?.length > 0 ? `${supervisors[0].name} ${supervisors[0].lastName}` : ''}
              InputProps={{ readOnly: true }}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={1} sx={{ my: 3 }}>
        <Grid item xs={3}>
          <FormControl fullWidth>
            <DatePicker
              label="Fecha de inicio"
              defaultValue={contract?.startedAt}
              readOnly
            />
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth>
            <DatePicker
              label="Fecha de terminación"
              defaultValue={getContractFieldWithNews('finishedAt')}
              readOnly
            />
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <FormControl fullWidth>
            <TextField
              label="Duración (En días)"
              defaultValue={getContractFieldWithNews('contractPeriod')}
              InputProps={{ readOnly: true }}
            />
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <FormControl fullWidth>
            <MoneyInput
              readOnly
              label="Valor de contrato"
              value={getContractFieldWithNews('contractBudget')}
            />
          </FormControl>
        </Grid>
        <Grid item xs={2}>
          <FormControl fullWidth>
            <TextField
              label="Riesgo ARL"
              defaultValue={ARL[contract?.arlLevel].text}
              InputProps={{ readOnly: true }}
            />
          </FormControl>
        </Grid>
        <Grid container spacing={1} sx={{ my: 3 }}>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <TextField
                label="Dependencia"
                defaultValue={contract?.dependency}
                InputProps={{ readOnly: true }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <TextField
                label="Naturaleza del contrato"
                defaultValue={contract?.nature}
                InputProps={{ readOnly: true }}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ my: 3 }}>
          <Typography variant="h6">Certificados</Typography>
          {affiliationLocked && isContractor && (
            <Typography variant="body2" color="text.secondary">
              La cuenta de cobro del Mes 1 está aprobada: los certificados de afiliación no se pueden modificar.
            </Typography>
          )}
        </Grid>
          {Object.keys(certificates).map((key) => (
            <Grid key={key} item xs={4}>
              {certificates[key] ? (
                <>
                  <Link
                    component="a"
                    href={certificates[key]}
                    target="_blank"
                  >
                    <Button
                      component="label"
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={26} /> : `Ver Certificado ${key.toUpperCase()}`}
                    </Button>
                  </Link>
                  {isContractor && !affiliationLocked
                    && <IconButton
                      color="primary"
                      onClick={() => setConfirmInfo({
                        open: true,
                        title: '¿Realmente quieres eliminar este certificado?',
                        data: { key }
                      })}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                </>
              ) : (
                <Button
                  component="label"
                  role={undefined}
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  disabled={!isContractor || status === CONTRACT_STATUS.CLOSED || affiliationLocked}
                >
                  Cargar Certificado {key.toUpperCase()}
                  <input
                    hidden
                    accept="*"
                    type="file"
                    onChange={(evt) => handleUploadCertificate(evt, key)}
                  />
                </Button>
              )}
            </Grid>
          ))
        }
      </Grid>
      <Grid container sx={{ my: 5 }}>
        <Grid item xs={9}>
          <Typography variant="h6">Seguimiento a Pagos</Typography>
          {balance !== 0 &&
            <Box>
              <Typography variant="h6" sx={{
                color: balance < 0 ? "#af635d" : balance > 1 ? "#a78316" : "#5e8d5e"
              }}>
                Balance: {currency(balance, { precision: 0 }).format()}
              </Typography>
              <small>Advertencia: El plan de pagos de este contrato, tiene un Balance diferente a cero</small>
            </Box>
          }
        </Grid>
        <Grid item xs={3}>
          <Button
            variant="contained"
            onClick={() => setShowSocialSecurity(true)}
          >
            <HealthAndSafetyIcon sx={{ mr: 2 }} />
            Seguridad Social
          </Button>
        </Grid>
      </Grid>
      <Grid container rowSpacing={2}>
        <ContractViewPaymentPlan
          contract={contract}
          setContract={setContract}
          getContractFieldWithNews={getContractFieldWithNews}
          getPeriodInfo={getPeriodInfo}
          setShowChargeAccountModal={setShowChargeAccountModal}
          setShowSocialSecurity={setShowSocialSecurity}
          setShowTemplate={setShowTemplate}
          setApprovedInfo={setApprovedInfo}
          setRevertInfo={setRevertInfo}
          cutoffReason={cutoffReason}
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 4 }}>
        {status === CONTRACT_STATUS.SUPERVISOR
          && <>
            <Button
              variant="contained"
              onClick={handleContractApproved}
            >
              Aprobar supervisión
            </Button>
          </>
        }
      </Grid>
    </Container>
  );
};

export default ContractView;
